import type { PillTone } from '../../components/StatusPill';
import type { ProjectOption } from '../../components/SearchableProjectDropdown/config';

// =======================================
// DATA TYPES
// =======================================

/**
 * Available timesheet status options
 */
export const TIMESHEET_STATUS_OPTIONS = ['Draft', 'Submitted', 'Approved', 'Rejected'] as const;

export type TimesheetStatus = typeof TIMESHEET_STATUS_OPTIONS[number];

/** StatusPill tone per batch status. */
export const BATCH_STATUS_TONE: Record<TimesheetStatus, PillTone> = {
  Draft: 'neutral',
  Submitted: 'info',
  Approved: 'success',
  Rejected: 'danger'
};

/**
 * Timesheet batch data structure as received from the database.
 * approved_at / approved_by are non-null exactly while status is Approved
 * (held in step by the enforce_timesheet_batch_transition trigger).
 */
export interface TimesheetBatch {
  id: number;
  employee_id: string;
  week_ending_date: string;
  status: TimesheetStatus;
  submitted_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  created_at: string;
  /** Present when fetched with the approver embed (see BATCH_SELECT). */
  approver?: { first_name: string; last_name: string } | null;
}

/**
 * Select list for a batch including who approved it. The FK name after `!`
 * is required: timesheet_batches has two FKs to team_members.
 */
export const BATCH_SELECT =
  '*, approver:team_members!timesheet_batches_approved_by_fkey(first_name, last_name)';

/**
 * Leave type enum
 */
export type LeaveType = 'personal' | 'sick_leave' | 'public_holiday';

export type EntryStatus = 'Pending' | 'Submitted' | 'Approved' | 'Rejected' | 'Allocated' | 'Hidden';

/**
 * One row of the timesheet_entries_with_projects view. This is the ONLY
 * entry shape the pages fetch; the week grid, the day view, the list and the
 * totals are all derived from it (timesheetDerive.ts), so they can never
 * disagree with each other mid-refetch.
 */
export interface TimesheetEntry {
  id: number;
  batch_id: number;
  project_id: number | null;
  date: string;
  hours: number;
  description: string | null;
  status: EntryStatus;
  leave_type: LeaveType | null;
  /** "HH:mm:ss" from Postgres `time`, or null for an hours-only entry. Both or neither. */
  start_time: string | null;
  end_time: string | null;
  /**
   * Which table project_id points at: true = stage, false = project. Null on
   * rows written before the flag existed; the view resolves those stage-first
   * and reports what it decided in project_type, which is what to read.
   */
  is_stage: boolean | null;
  created_at: string;
  updated_at: string | null;
  // Resolved by the view. For a leave entry these read 'Unknown Project' /
  // 'N/A' / 'unknown'; use leave_type first.
  project_name: string;
  project_job_number: string;
  project_type: 'project' | 'stage' | 'unknown';
  parent_project_id: number | null;
  stage_description: string | null;
  parent_description: string | null;
  /**
   * The lunch break of this row's day, resolved by the view: the day's own
   * timesheet_day_lunches row if it has one (lunch_is_custom), otherwise the
   * employee's usual break. Every row on a day carries the same values. Read
   * it through dayLunch() in timesheetDerive.ts.
   */
  lunch_start_time: string;
  lunch_minutes: number;
  lunch_is_custom: boolean;
}

/** What a page sends to create an entry. batch_id is added by the hook. */
export interface TimesheetEntryInput {
  project_id: number | null;
  /** true for a stage, false for a project, null for leave. Never omit it: project and stage ids overlap. */
  is_stage: boolean | null;
  leave_type: LeaveType | null;
  date: string;
  hours: number;
  description: string | null;
  start_time: string | null;
  end_time: string | null;
}

export type TimesheetEntryPatch = Partial<TimesheetEntryInput>;

/**
 * One row of the week grid: a project, a stage or a leave type, with the
 * week's entries for it. A row with no entries is a "blank row" the user
 * added from the Recent strip so they can type straight into its cells; it
 * lives only in page state until a cell is saved.
 */
export interface ProjectGroup {
  /** `project_<id>`, `stage_<id>` or `leave_<type>`; the grid row key and the blank-row identity. */
  groupKey: string;
  projectId: number | null;
  leaveType: LeaveType | null;
  isLeave: boolean;
  /** Display name: stage description, project description, or the leave label. */
  projectName: string;
  /** "12345" or "12345.2"; null for leave. */
  jobNumber: string | null;
  entityType: 'project' | 'stage' | 'leave';
  parentProjectId: number | null;
  stageDescription: string | null;
  parentDescription: string | null;
  entries: TimesheetEntry[];
}

/** The page-level view switch. `week` is the grid. */
export type TimesheetView = 'day' | 'week' | 'list';

/**
 * What the entry editor opens with when creating: a cell, a chip, a day.
 */
export interface EntryEditorDefaults {
  date?: string;
  projectOption?: ProjectOption | null;
  group?: ProjectGroup | null;
  leaveType?: LeaveType | null;
  startTime?: string | null;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Days of the week for timesheet entry
 */
export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
] as const;

export type DayOfWeek = typeof DAYS_OF_WEEK[number];

/**
 * Leave type options for the entry editor and the Recent strip
 */
export const LEAVE_TYPE_OPTIONS = [
  { value: 'personal', label: 'Annual Leave', trackBalance: true },
  { value: 'sick_leave', label: 'Sick Leave', trackBalance: true },
  { value: 'public_holiday', label: 'Public Holiday', trackBalance: false }
] as const;

/**
 * Leave type labels for display
 */
export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  personal: 'Annual Leave',
  sick_leave: 'Sick Leave',
  public_holiday: 'Public Holiday'
};

/** Fallback when an employee's standard day is not loaded yet. */
export const DEFAULT_STANDARD_HOURS = 7.5;

/** Fallback for team_members.default_start_time while it is not loaded yet. */
export const DEFAULT_DAY_START = '08:30';

/** Fallback for team_members.lunch_start_time / lunch_minutes while not loaded. */
export const DEFAULT_LUNCH = { start: '12:00', minutes: 60 };
